import { Edge, Handle, MarkerType, Node, NodeProps, Position, ReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import '../styles/ApprovalWorkflow.scss';

import { CheckmarkFilled, ClockFilled, Comment24Filled, DismissFilled, Info24Filled } from '@fluentui/react-icons';
import resolveFunctionNames from '../helpers/resolveFunctionNames.helper';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  AvatarNamedColor,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import { Avatar as UserAvatar, useUsers } from '@modules/users';
import {
  ApprovalStatus,
  ApprovalUserStatus,
  TApproval,
  TApprovalPhaseKey,
  TApprovalUser,
  TApprovalUserDialogState,
} from '../types';
import getEnabledPhases from '../helpers/getEnabledPhases.helper';

const APPROVAL_STATUSES = {
  PENDING: { color: tokens.colorNeutralForeground1, icon: <ClockFilled />, label: 'Pending' },
  ACCEPTED: { color: tokens.colorBrandForeground1, icon: <CheckmarkFilled />, label: 'Accepted' },
  WAITING: { color: tokens.colorNeutralForeground4, icon: '', label: '-' },
  DECLINED: { color: tokens.colorStatusDangerForeground1, icon: <DismissFilled />, label: 'Declined' },
  CANCELED: { color: tokens.colorNeutralForeground1, icon: <DismissFilled />, label: 'Canceled' },
};

interface PhaseNodeProps {
  data: {
    label: string;
    status: 'ACCEPTED' | 'PENDING' | 'DECLINED' | 'CANCELED' | 'WAITING';
  };
}

// Custom node component for phases
const PhaseNode: FC<PhaseNodeProps> = ({ data }) => {
  const status = APPROVAL_STATUSES[data.status];

  return (
    <div className="phase-node nodrag">
      <Handle type="target" position={Position.Left} style={{ visibility: 'hidden', top: '108px' }} />
      <Handle type="source" position={Position.Right} style={{ visibility: 'hidden', top: '108px' }} />
      <div className="phase-label">{data.label}</div>
      <div className={`phase-rectangle`}>
        <div
          className={`status-ring ${data.status === 'PENDING' ? 'pulsing' : ''}`}
          style={{ borderColor: status.color }}
        >
          <div className="status-icon" style={{ color: status.color }}>
            {status.icon}
          </div>
        </div>
        <div className="status-text" style={{ color: status.color }}>
          {status.label}
        </div>
      </div>
    </div>
  );
};

interface UserNodeProps extends NodeProps {
  data: {
    approvalUser: TApprovalUser;
    isPhasePending: boolean;
    phaseName: TApprovalPhaseKey;
    approvalUserDialogState?: TApprovalUserDialogState;
    setApprovalUserDialogState: (state?: TApprovalUserDialogState) => void;
    groupNameById?: Map<string, string>;
  };
}

const useStyles = makeStyles({
  avatar: {
    '::before': {
      backgroundColor: 'white',
    },
    ':hover::before': {
      ...shorthands.borderColor(tokens.colorBrandBackground),
      ...shorthands.borderWidth('3px'),
    },
  },
  selectedAvatar: {
    '::before': {
      ...shorthands.borderColor(tokens.colorBrandBackground),
      ...shorthands.borderWidth('3px'),
    },
  },
});

const UserNode: FC<UserNodeProps> = ({ data }) => {
  const {
    approvalUser,
    isPhasePending,
    phaseName,
    approvalUserDialogState,
    setApprovalUserDialogState,
    groupNameById,
  } = data;
  const { userMap } = useUsers();
  const user = userMap ? userMap[approvalUser.user] : undefined;
  const isSelected = approvalUserDialogState?.isOpen && approvalUser === approvalUserDialogState.approvalUser;

  const functionNames = useMemo(
    () => (groupNameById ? resolveFunctionNames(approvalUser.customGroupIds, groupNameById) : ''),
    [groupNameById, approvalUser.customGroupIds]
  );

  const badgeStatus =
    approvalUser.status === ApprovalUserStatus.ACCEPTED
      ? 'available'
      : approvalUser.status === ApprovalUserStatus.DECLINED
        ? 'busy'
        : isPhasePending
          ? 'away'
          : 'unknown';

  const classes = useStyles();
  const avatarContent = (
    <div
      style={{ position: 'relative' }}
      onClick={() => {
        setApprovalUserDialogState(isSelected ? undefined : { approvalUser, phaseName, isOpen: true });
      }}
    >
      <UserAvatar
        user={user}
        size={40}
        badge={{ status: badgeStatus, size: 'medium' }}
        activeAppearance="ring-shadow"
        active="active"
        color={isSelected ? 'brand' : 'colorful'}
        className={mergeClasses(classes.avatar, isSelected ? classes.selectedAvatar : '')}
      />
      {approvalUser.note && (
        <Comment24Filled
          style={{
            position: 'absolute',
            top: '-18px',
            right: '-19px',
            color:
              approvalUser.status === ApprovalUserStatus.ACCEPTED
                ? tokens.colorPaletteLightGreenForeground3
                : tokens.colorPaletteRedBackground3,
          }}
        />
      )}
    </div>
  );

  if (functionNames) {
    return (
      <Tooltip content={`${user?.name || 'Unknown'} – ${functionNames}`} relationship="label" withArrow>
        {avatarContent}
      </Tooltip>
    );
  }
  return avatarContent;
};

interface InfoNodeProps extends NodeProps {
  data: {
    text: string;
  };
}

const InfoNode: FC<InfoNodeProps> = ({ data }) => {
  const { text } = data;

  const classes = useStyles();
  return (
    <Tooltip content={text} relationship="description" withArrow>
      <Avatar
        icon={<Info24Filled style={{ color: tokens.colorBrandBackground }} />}
        size={40}
        activeAppearance="ring-shadow"
        active="active"
        color={'white' as AvatarNamedColor}
        className={classes.avatar}
      />
    </Tooltip>
  );
};

const NODE_TYPES = {
  phaseNode: PhaseNode,
  userNode: UserNode,
  infoNode: InfoNode,
};

type TProps = {
  approval: TApproval;
  approvalUserDialogState?: TApprovalUserDialogState;
  setApprovalUserDialogState: (state?: TApprovalUserDialogState) => void;
  groupNameById?: Map<string, string>;
};

const ApprovalWorkflow = ({ approval, approvalUserDialogState, setApprovalUserDialogState, groupNameById }: TProps) => {
  const enabledPhases = useMemo(() => getEnabledPhases(approval.phases), [approval.phases]);

  const nodes: Node[] = useMemo(() => {
    const PHASE_LABELS: Record<TApprovalPhaseKey, string> = {
      AUTHORING: 'Authoring',
      REVIEWING: 'Reviewing',
      APPROVING: 'Approving',
    };

    const isPhaseAccepted = (phaseKey: TApprovalPhaseKey): boolean => {
      const phase = approval.phases[phaseKey];
      return Boolean(phase && phase.users.every((u) => u.status === ApprovalUserStatus.ACCEPTED));
    };

    const getPhaseStatus = (phaseKey: TApprovalPhaseKey, phaseIdx: number): PhaseNodeProps['data']['status'] => {
      const isCurrent = approval.status === phaseKey;
      if (isCurrent) return 'PENDING';

      // Check if all previous enabled phases are accepted
      const allPreviousAccepted = enabledPhases.slice(0, phaseIdx).every(isPhaseAccepted);
      if (!allPreviousAccepted) return 'WAITING';

      if (isPhaseAccepted(phaseKey)) return 'ACCEPTED';
      if (approval.status === ApprovalStatus.APPROVED) return 'ACCEPTED';
      if (approval.status === ApprovalStatus.DECLINED) return 'DECLINED';
      return 'CANCELED';
    };

    const X_OFFSET = 300;
    const nodes: Node[] = [];

    // Phase nodes
    enabledPhases.forEach((phaseKey, phaseIdx) => {
      nodes.push({
        id: phaseKey.toLowerCase(),
        type: 'phaseNode',
        position: { x: phaseIdx * X_OFFSET, y: 0 },
        data: {
          label: PHASE_LABELS[phaseKey],
          status: getPhaseStatus(phaseKey, phaseIdx),
        },
        selectable: false,
      });
    });

    // User nodes per phase
    const ROW_SIZE = 4;
    enabledPhases.forEach((phaseName, phaseIdx) => {
      const phase = approval.phases[phaseName];
      if (!phase) return;

      let rowIdx = -1;
      const infoLabel = phaseName === 'AUTHORING' && approval.violationNote ? approval.violationNote : '';
      const isFull = phase.users.length % ROW_SIZE === 0;

      let rowOffset = 40;
      for (let rowItemIdx = 0; rowItemIdx < phase.users.length; rowItemIdx += ROW_SIZE) {
        rowIdx++;
        const userRow = phase.users.slice(rowItemIdx, rowItemIdx + ROW_SIZE);
        rowOffset = (userRow.length / 2 - 0.5) * -60 + 40;

        if (infoLabel && !isFull && rowItemIdx + ROW_SIZE >= phase.users.length) rowOffset -= 30;

        userRow.forEach((phaseUser, colIdx) => {
          nodes.push({
            id: `user-${phaseIdx}-${rowItemIdx + colIdx}`,
            type: 'userNode',
            position: { x: phaseIdx * X_OFFSET + rowOffset + colIdx * 60, y: 210 + rowIdx * 60 },
            data: {
              approvalUser: phaseUser,
              isPhasePending: phaseName === approval.status,
              phaseName,
              approvalUserDialogState,
              setApprovalUserDialogState,
              groupNameById,
            },
          });
        });
      }

      if (infoLabel) {
        const colIdx = phase.users.length % ROW_SIZE;
        if (isFull) {
          rowOffset = 40;
          rowIdx++;
        }

        nodes.push({
          id: `info-${phaseIdx}`,
          type: 'infoNode',
          position: { x: phaseIdx * X_OFFSET + rowOffset + colIdx * 60, y: 210 + rowIdx * 60 },
          data: {
            text: infoLabel,
          },
        });
      }
    });

    return nodes;
  }, [approval, enabledPhases, approvalUserDialogState, setApprovalUserDialogState, groupNameById]);

  const [edges, setEdges] = useState<Edge[]>([]);
  const refApproval = useRef<TApproval>(approval);
  refApproval.current = approval;

  const updateEdges = useCallback(() => {
    const currentApproval = refApproval.current;
    const phases = getEnabledPhases(currentApproval.phases);
    const currentPhaseIdx = phases.indexOf(currentApproval.status as TApprovalPhaseKey);

    const newEdges: Edge[] = [];
    for (let i = 0; i < phases.length - 1; i++) {
      const sourcePhase = phases[i];
      const targetPhase = phases[i + 1];
      const isSourceCompleted = Boolean(
        currentApproval.phases[sourcePhase]?.users.every((user) => user.status === ApprovalUserStatus.ACCEPTED)
      );
      const isPast = currentPhaseIdx > i || isSourceCompleted;

      newEdges.push({
        id: `e-${sourcePhase}-${targetPhase}`,
        source: sourcePhase.toLowerCase(),
        target: targetPhase.toLowerCase(),
        type: 'default',
        style: {
          strokeWidth: 3,
          stroke: isPast ? tokens.colorBrandForeground1 : tokens.colorNeutralForeground4,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 14,
          height: 14,
          color: isPast ? tokens.colorBrandForeground1 : tokens.colorNeutralForeground4,
        },
        selectable: false,
      });
    }
    setEdges(newEdges);
  }, []);

  useEffect(() => {
    if (edges.length > 0) {
      updateEdges();
      return;
    }

    const timeoutId = setTimeout(updateEdges, 100);
    return () => clearTimeout(timeoutId);
  }, [approval.status, approval.phases, updateEdges]);

  return (
    <div style={{ width: '100%', height: '300px' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{
          padding: 0.2,
        }}
        nodeTypes={NODE_TYPES}
        nodesDraggable={false} // Disable node dragging
        nodesConnectable={false} // Disable node connecting
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        panOnScroll={false}
        panOnDrag={false}
        elementsSelectable
        style={{ cursor: 'default' }}
        proOptions={{ hideAttribution: true }}
      ></ReactFlow>
    </div>
  );
};

export default ApprovalWorkflow;
